import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMount } from 'react-use'
import Loading from 'src/components/templates/Loading'
import { AuthContext } from 'src/context/auth.context'
import { ToastTriggerContext } from 'src/context/toast.context'
import { AdminAdminStoreManagerRoles, isOneOfRoles } from 'src/domain/role'
import { useApiMember } from 'src/hooks/useApiMember'
import useAppTitle from 'src/hooks/useAppTitle'
import useBeforeUnload from 'src/hooks/useBeforeUnload'
import Path, { OrgPathParams } from 'src/routes/path'
import { Member, RoleEnum, Store as EntityStore } from 'src/slices/services/api'
import {
  getStores,
  selectStoresByParams,
  selectStoresRequestStateByParams,
} from 'src/slices/stores/storesSlice'
import { useAppDispatch, useAppSelector } from 'src/store'
import TemplatesMembersUpdate from '../../templates/Update'
import { adjustMembers } from './presenter'

const PagesMembersUpdate: React.FC = (): ReactElement => {
  useAppTitle('ユーザー管理')
  useBeforeUnload()
  const authContext = useContext(AuthContext)
  const { getMembers, patchMembers } = useApiMember()
  const toastContex = useContext(ToastTriggerContext)
  const [members, setMembers] = useState<Member[]>([])
  const [isLoaded, setLoaded] = useState<boolean>(false)
  const { orgCode } = useParams<OrgPathParams>() as OrgPathParams
  const navigate = useNavigate()

  const param = useMemo(() => {
    return { orgCode, page: 1, perPage: 1000 }
  }, [orgCode])

  const dispatch = useAppDispatch()
  const stores: EntityStore[] =
    useAppSelector((state) => selectStoresByParams(param)(state)) ?? []

  const { status } = useAppSelector((state) =>
    selectStoresRequestStateByParams(param)(state)
  )

  const selfRole: RoleEnum | undefined = useAppSelector(
    (state) => state.auth.currentAuthorization?.role
  )

  useEffect(() => {
    if (status === 'idle') {
      dispatch(getStores(param))
    }
  }, [dispatch, status, param])

  const handleUpdateMember = async (data: Member[]) => {
    const result = await patchMembers(orgCode, data)
    if (!result.success) {
      toastContex.sendToast({
        variant: 'error',
        title: `ユーザーの更新に失敗しました: ${result.message}`,
      })
      return
    }
    toastContex.sendToast({
      variant: 'success',
      title: `ユーザーの更新に成功しました`,
    })
    navigate(Path.members(orgCode))
  }

  const onClickConfirmClose = () => {
    navigate(Path.members(orgCode))
  }

  useMount(async () => {
    const result = await getMembers(orgCode)
    if (!result.success) {
      toastContex.sendToast({
        variant: 'error',
        title: `ユーザーの取得に失敗しました: ${result.message}`,
      })
      return
    }
    const selfEmail = authContext.getUserEmail()
    if (!selfRole || !selfEmail) {
      return
    }
    const adjustedMembers = adjustMembers(
      result.data || [],
      selfEmail,
      selfRole
    )
    if (!adjustedMembers) {
      toastContex.sendToast({
        variant: 'error',
        title: `再度ログインしなおしてからお試しください`,
      })
    }
    setMembers(adjustedMembers || [])
    setLoaded(true)
  })

  const showSwitchByRole = useCallback((): boolean => {
    return isOneOfRoles(AdminAdminStoreManagerRoles, selfRole)
  }, [selfRole])

  return (
    <div>
      {!showSwitchByRole() && <div>権限がありません</div>}

      {isLoaded && status === 'succeeded' && selfRole && showSwitchByRole() ? (
        <TemplatesMembersUpdate
          stores={stores}
          members={members}
          selfRole={selfRole}
          orgCode={orgCode}
          onUpdateMember={handleUpdateMember}
          onClickConfirmClose={onClickConfirmClose}
        />
      ) : (
        <Loading />
      )}
    </div>
  )
}

export default PagesMembersUpdate
